import { useState, useEffect } from 'react';
import { findIndex, isEmpty, uniqBy } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { useImmer } from 'use-immer';
import {
  validateAssetName,
  createUniqueName,
  findSubTree,
  formatAssetNavigationDataSetWiz,
  processAssetPlaceholders,
  getSubTypesForPlaceholder,
} from 'utils/index';

export default (formattedAssets, templateAssets, assetTypes, values) => {
  const [textFieldError, setTextFieldError] = useState(null);
  const [selectedAssets, setSelectedAssets] = useImmer([
    ...values.selectedAssets,
  ]);
  const [selectedAsset, setSelectedAsset] = useState({});
  const [assetNodes, setAssetNodes] = useState([]);
  const [expanded, setExpanded] = useImmer([...values.expanded]);
  const [assetsForTemplate, setAssetsForTemplate] = useState([
    ...values.allAssets,
  ]);

  const getChildren = (childAssets, childArray) => {
    if (!isEmpty(childAssets)) {
      childAssets.forEach(asset => {
        if (asset?.isAssetInstance) childArray.push(asset);
        getChildren(asset?.children, childArray);
      });
    }
    return childArray;
  };
  const findParentNode = (assetNodesObj, parentAssetId) => {
    if (assetNodesObj.id === parentAssetId) return assetNodesObj;
    if (!isEmpty(assetNodesObj.children)) {
      const results = assetNodesObj.children.map(child =>
        findParentNode(child, parentAssetId)
      );
      const foundResult = results.find(result => result !== null);
      return foundResult || null;
    }
    return null;
  };

  const uniqSelectedAssets = (array1, array2) =>
    uniqBy([...array1, ...array2], 'id');

  const createNewAssets = (newTemplateAssets, parentId, newAssets) => {
    const createAsset = templateAsset => {
      const newOne = {
        id: uuidv4(),
        name: `${
          !isEmpty(templateAsset.assetSubType)
            ? `${templateAsset.assetSubType.name}`
            : createUniqueName(
                templateAsset.defaultName,
                templateAsset.children
                  ? templateAsset.children.map(child => child?.name)
                  : []
              )
        }`,
        new: true,
        assetType: templateAsset.assetType,
        assetSubType: templateAsset.assetSubType,
        parent: parentId || null,
        children: [],
        deletedAt: null,
        parentAssetPlaceholderId: templateAsset.parentAssetPlaceholderId,
      };

      newAssets.push(newOne);
      const parentIndex = findIndex(newAssets, asset => asset.id === parentId);
      if (parentIndex !== -1) {
        newAssets[parentIndex].children.push(newOne.id);
      }
      if (!isEmpty(templateAsset.children)) {
        createNewAssets(templateAsset.children, newOne.id, newAssets);
      }
    };

    newTemplateAssets.forEach(templateAsset => {
      createAsset(templateAsset);
    });
  };

  const getChildStructure = (
    tree,
    typeId,
    assetSubTypeId,
    parentId,
    parentAssetPlaceholderId,
    newAssets
  ) => {
    if (
      (tree?.assetType?.id === typeId || tree?.assetTypeId === typeId) &&
      !isEmpty(tree.children) &&
      parentAssetPlaceholderId === tree.parentAssetPlaceholderId &&
      tree.assetSubType?.id === assetSubTypeId
    ) {
      createNewAssets(tree.children, parentId, newAssets);
    } else if (!isEmpty(tree.children)) {
      tree.children?.forEach(childTree => {
        getChildStructure(
          childTree,
          typeId,
          childTree.assetSubType?.id,
          parentId,
          childTree.parentAssetPlaceholderId,
          newAssets
        );
      });
    }
  };

  const onAddAssetClick = node => {
    const newAssets = [];
    if (node.possibleAssetSubTypes.length > 1) {
      const existingChildrenSubTypes = node.children.map(
        child => child.assetSubType.id
      );
      const newSubTypesToAdd = node.possibleAssetSubTypes.filter(
        nodeSubType => !existingChildrenSubTypes.includes(nodeSubType.id)
      );
      newSubTypesToAdd.forEach(assetPlaceholderSubType => {
        const newOne = {
          id: uuidv4(),
          name: `${assetPlaceholderSubType.name}`,
          new: true,
          assetType: {
            ...node.assetType,
          },
          assetSubType: { ...assetPlaceholderSubType },
          parent: node.parent,
          children: [],
          deletedAt: null,
          parentAssetPlaceholderId: node.parentAssetPlaceholderId,
        };
        newAssets.push(newOne);

        const templateSubTree = findSubTree(
          formattedAssets,
          node.assetType.id,
          assetPlaceholderSubType.id,
          node.parentAssetPlaceholderId
        );

        getChildStructure(
          templateSubTree,
          node.assetType.id,
          assetPlaceholderSubType.id,
          newOne.id,
          node.parentAssetPlaceholderId,
          newAssets
        );
      });
    } else {
      const newOne = {
        id: uuidv4(),
        name: `${
          node.assetSubType
            ? `${node.assetSubType.name}`
            : createUniqueName(
                node.defaultName,
                node.children ? node.children.map(child => child?.name) : []
              )
        }`,
        new: true,
        assetType: {
          ...node.assetType,
        },
        assetSubType: { ...node?.assetSubType } || null,
        parent: node.parent || null,
        children: [],
        deletedAt: null,
        parentAssetPlaceholderId: node.parentAssetPlaceholderId,
      };
      newAssets.push(newOne);
      const templateSubTree = findSubTree(
        formattedAssets,
        node.assetType.id,
        node.assetSubType?.id,
        node.parentAssetPlaceholderId
      );

      getChildStructure(
        templateSubTree,
        node.assetType.id,
        node.assetSubType?.id,
        newOne.id,
        node.parentAssetPlaceholderId,
        newAssets
      );
    }
    setAssetsForTemplate(curr => [...curr, ...newAssets]);
    const newExpandedNodes = newAssets.map(asset => asset.id);
    const newExpanded = new Set([
      ...values.expanded,
      ...newExpandedNodes,
      node.id,
    ]);
    setExpanded([...newExpanded]);
  };

  const onEditClick = assetId => {
    const index = findIndex(assetsForTemplate, asset => asset.id === assetId);
    setAssetsForTemplate(curr => {
      const newAssets = [...curr];
      newAssets[index] = { ...newAssets[index], edited: true };
      return newAssets;
    });
  };

  const onRemoveAssetClick = node => {
    const childrenIds = getChildren(node.children, [node]).map(({ id }) => id);
    const childrenIdSet = new Set(childrenIds);
    setAssetsForTemplate(curr => {
      const allAssetsWithoutRemoved = curr.filter(asset => {
        return !childrenIdSet.has(asset.id);
      });
      return [...allAssetsWithoutRemoved];
    });

    setSelectedAssets(curr => {
      const selectedAssetsWithoutRemoved = curr.filter(asset => {
        return !childrenIdSet.has(asset.id);
      });
      return [...selectedAssetsWithoutRemoved];
    });

    setExpanded(curr => {
      const expandedAssetsWithoutRemoved = curr.filter(asset => {
        return ![...childrenIdSet].includes(asset);
      });
      return [...expandedAssetsWithoutRemoved];
    });
  };

  const onClearClick = assetId => {
    const index = findIndex(assetsForTemplate, asset => asset.id === assetId);
    setAssetsForTemplate(curr => {
      const newAssets = [...curr];
      newAssets[index] = { ...newAssets[index], edited: false };
      return newAssets;
    });
  };

  const onDoneClick = (node, editingValue) => {
    const errors = validateAssetName(
      editingValue,
      node.parent || null,
      node.assetType?.id || null,
      node.assetSubType?.id || null,
      assetsForTemplate
    );
    setTextFieldError(errors);
    if (!errors) {
      const index = findIndex(assetsForTemplate, asset => asset.id === node.id);
      setAssetsForTemplate(curr => {
        const newAssets = [...curr];
        newAssets[index] = {
          ...newAssets[index],
          edited: false,
          name: editingValue,
          editedName: true,
        };
        return newAssets;
      });
      const selectedAssetIndex = findIndex(
        selectedAssets,
        asset => asset.id === node.id
      );
      if (selectedAssetIndex > -1) {
        setSelectedAssets(curr => {
          curr[selectedAssetIndex].name = editingValue;
          return [...curr];
        });
      }
    }
  };

  const getParent = (parentId, parentArray) => {
    if (parentId) {
      const parent = assetsForTemplate.find(asset => {
        return asset.id === parentId;
      });

      const parentNode = findParentNode(assetNodes[0], parentId);
      if (parent?.id !== undefined && parentNode) {
        parentArray.push(parentNode);
      }
      getParent(parent?.parent, parentArray);
    }
    return parentArray;
  };

  const updateSelection = node => {
    const assetIndex = findIndex(selectedAssets, asset => asset.id === node.id);
    if (assetIndex > -1) {
      const removeAsset = getChildren(node.children, [node]);
      setSelectedAssets(curr => {
        const assetIdsToRemove = new Set(removeAsset.map(item => item.id));
        const filteredArray = curr.filter(
          item => !assetIdsToRemove.has(item.id)
        );
        return filteredArray;
      });
    } else {
      const checkAssetTypeAlreadySelected = selectedAssets.filter(asset => {
        return (
          (asset.assetType.id === node.assetType.id &&
            asset?.assetSubType?.id === node?.assetSubType?.id) ||
          asset.parentAssetPlaceholderId === node.parentAssetPlaceholderId
        );
      });
      const findParentAssetPlaceholder = templateAssets.find(
        templateAsset => templateAsset.id === node.parentAssetPlaceholderId
      );
      if (
        checkAssetTypeAlreadySelected.length >= 1 &&
        node.assetTypeMaxCount === 1 &&
        !node.multipleSubtypes &&
        node.parentAssetPlaceholderId ===
          checkAssetTypeAlreadySelected[0].parentAssetPlaceholderId &&
        findParentAssetPlaceholder?.maxCount !== null
      ) {
        const parentNode = findParentNode(
          assetNodes[0],
          checkAssetTypeAlreadySelected[0].id
        );
        const nodeChildrenToRemove = getChildren(parentNode.children, [
          parentNode,
        ]);
        const nodeParentsToRemove = getParent(
          checkAssetTypeAlreadySelected[0].parent,
          [checkAssetTypeAlreadySelected[0]]
        );
        setSelectedAssets(curr => {
          const assetIdsToRemove = new Set(
            nodeChildrenToRemove
              .concat(nodeParentsToRemove)
              .map(item => item.id)
          );
          const filteredArray = curr.filter(item => {
            return !assetIdsToRemove.has(item.id);
          });
          const parents = getParent(node.parent, [node]);

          const updatedSelectedAssets = uniqSelectedAssets(
            filteredArray,
            parents
          );

          return updatedSelectedAssets;
        });
      } else if (
        checkAssetTypeAlreadySelected.length >= 1 &&
        node.assetTypeMaxCount === 1 &&
        node.multipleSubtypes
      ) {
        setSelectedAssets(curr => {
          const assetsAlreadySelectedWithDiffParent =
            checkAssetTypeAlreadySelected.filter(x => x.parent !== node.parent);
          const parentsToRemove = assetsAlreadySelectedWithDiffParent
            .map(x => getParent(x.parent, [x]))
            .flat(2);
          const childrenToRemove = parentsToRemove
            .map(parent => getChildren(parent.children, [parent]))
            .flat(2);
          const nodeParentsToAdd = getParent(node.parent, [node]);
          const assetIdsToRemove = new Set(
            childrenToRemove.map(item => item.id)
          );
          const filteredArray = curr.filter(item => {
            return !assetIdsToRemove.has(item.id);
          });
          const updatedSelectedAssets = uniqSelectedAssets(
            filteredArray,
            nodeParentsToAdd
          );
          return updatedSelectedAssets;
        });
      } else {
        const parents = getParent(node.parent, [node]);
        const parentsSameAssetType = parents
          .map(parent => {
            let sameAssetTypes = selectedAssets.filter(
              asset =>
                asset.assetType.id === parent.assetType.id &&
                asset.id !== parent.id &&
                asset.assetTypeMaxCount === 1 &&
                !asset.multipleSubtypes &&
                asset.parentAssetPlaceholderId ===
                  parent.parentAssetPlaceholderId
            );
            // get the parent of sameAssetType
            // if the parent has assetTypeMaxCount as null, then remove the sameAssetType
            // as it means more than one parent can be selected and therefore its children
            // don't have to be deselected
            const parentOfSameAssetType = selectedAssets.find(sa => {
              return sa.id === sameAssetTypes[0]?.parent;
            });
            if (parentOfSameAssetType?.assetTypeMaxCount === null) {
              sameAssetTypes = [];
            }
            const updatedNodesInfo = sameAssetTypes.map(asset => {
              return findParentNode(assetNodes[0], asset.id);
            });
            const childrenOfSameAssetType = updatedNodesInfo.map(
              sameAssetType =>
                getChildren(sameAssetType.children, [sameAssetType])
            );
            return childrenOfSameAssetType;
          })
          .flat(2);
        setSelectedAssets(curr => {
          const assetIdsToRemove = new Set(
            parentsSameAssetType.map(item => item.id)
          );

          const filteredArray = curr.filter(item => {
            return !assetIdsToRemove.has(item.id);
          });
          const updatedSelectedAssets = uniqSelectedAssets(
            filteredArray,
            parents
          );
          return updatedSelectedAssets;
        });
      }
    }
  };

  useEffect(() => {
    // unused code commented out for now to test with always expanding only 1 level of depth

    // const templateLevels = templateAssets.filter(
    //   templateAsset => !isEmpty(templateAsset.assetType)
    // ).length;

    const depthCounter = 1;

    // if (templateLevels >= 5) depthCounter = 3;
    // else depthCounter = 2;

    const createInitialExpandedNodes = (nodes, counter = depthCounter) => {
      if (counter < 1) return [];
      const expandedNodes = [];
      expandedNodes.push(nodes?.id);
      if (!isEmpty(nodes?.children)) {
        nodes.children.forEach(child => {
          expandedNodes.push(...createInitialExpandedNodes(child, counter - 1));
        });
      }
      return expandedNodes;
    };
    const initialExpandedNodes = createInitialExpandedNodes(assetNodes[0]);
    const newExpanded = new Set([...values.expanded, ...initialExpandedNodes]);
    setExpanded([...newExpanded]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetNodes]);

  useEffect(() => {
    if (assetTypes.length) {
      let projectAssetsMatchingTemplateAssetTypeIds = [];

      const topAsset = templateAssets.find(
        asset => !asset.parentAssetPlaceholderId
      );

      if (!isEmpty(assetsForTemplate)) {
        const phDict = Object.fromEntries(
          templateAssets.map(a => [a.id, { possibleAssets: [] }])
        );
        const root = templateAssets.find(a => !a.parentAssetPlaceholderId);

        processAssetPlaceholders(
          phDict,
          assetsForTemplate,
          templateAssets,
          root
        );

        const possibleAssetInstances = templateAssets.flatMap(
          a => phDict[a.id].possibleAssets
        );

        // check if all asset children are in the list of possible assets
        // if not set the children on the asset to an empty array
        const possibleAssetIds = possibleAssetInstances.map(a => a.id);
        const allAssetInstances = possibleAssetInstances.map(
          possibleInstance => {
            const hasAllChildren = possibleInstance.children.every(child =>
              possibleAssetIds.includes(child)
            );

            if (hasAllChildren) {
              return possibleInstance;
            }
            return {
              ...possibleInstance,
              children: [],
            };
          }
        );

        projectAssetsMatchingTemplateAssetTypeIds = allAssetInstances;
      }

      const assetTypesWithInfo = assetTypes.reduce((acc, curr) => {
        const parent = assetTypes.find(
          asset => asset.id === curr.parentAssetPlaceholderId
        );
        const subTypes = getSubTypesForPlaceholder(curr, templateAssets);
        acc.push({
          ...curr,
          assetType: curr.assetType,
          assetSubType: curr.assetSubType,
          possibleAssetSubTypes: curr.assetSubType ? subTypes : [],
          parent: parent?.id || null,
          isTypeOnly: true,
        });
        return acc;
      }, []);

      const nodes = formatAssetNavigationDataSetWiz(
        [...projectAssetsMatchingTemplateAssetTypeIds, ...assetTypesWithInfo],
        null,
        topAsset?.assetType?.id ? topAsset.assetType.id : null,
        !!topAsset?.assetType?.id,
        null
      );
      setAssetNodes(nodes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetsForTemplate, assetTypes]);

  return {
    updateSelection,
    onAddAssetClick,
    onEditClick,
    onDoneClick,
    onClearClick,
    textFieldError,
    selectedAsset,
    setSelectedAsset,
    onRemoveAssetClick,
    expanded,
    setExpanded,
    assetNodes,
    setAssetNodes,
    selectedAssets,
    assetsForTemplate,
  };
};
